import type { FC } from 'react';
import React, { useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { alpha, Button, Chip, Popover, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Iconify } from '../Iconify';
import type { FilterDefinition } from './declarations/type';
import { FilterSwitch } from './Filters';
import { useFilterLabel } from './ActiveFilterLabel';
import { canUnsetFilter, isFilterSet, unsetFilter } from './filterState';
import { TogglePill } from './TogglePill';

interface FilterPillsProps {
  items: Array<FilterDefinition<unknown>>;
  onChange: (value: unknown) => void;
  // Clears every filter of the table, not only the ones rendered here.
  clearAll?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * A row of filter chips. Clicking a chip opens its editor in a popover: a
 * select filter opens its options directly, other types their input. A set
 * chip shows its value and can be cleared from its icon. The table renders one
 * instance in its header for highlighted filters and one in the collapsible
 * filter row for the rest.
 */
export const FilterPills: FC<FilterPillsProps> = ({
  items,
  onChange,
  clearAll,
  sx,
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<{
    key: unknown;
    el: HTMLElement;
  } | null>(null);
  const openFilter = items.find((filter) => filter.key === anchor?.key);

  const close = () => setAnchor(null);

  return (
    <Stack
      sx={[
        {
          gap: 1.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        direction="row"
        sx={{
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {items.map((filter) => (
          <FilterPill
            key={String(filter.key)}
            filter={filter}
            open={filter.key === anchor?.key}
            onOpen={(el) => setAnchor({ key: filter.key, el })}
          />
        ))}
        {clearAll && (
          <Button size="small" onClick={clearAll}>
            {t('common.clear-all')}
          </Button>
        )}
      </Stack>
      <Popover
        open={Boolean(openFilter)}
        anchorEl={anchor?.el}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: -8, horizontal: 'left' }}
        slotProps={{
          paper: {
            // The select menu brings its own padding and width.
            sx: (theme) =>
              openFilter?.type === 'select'
                ? {
                    borderRadius: '12px',
                    boxShadow: `0 12px 32px ${alpha(
                      theme.palette.primary.main,
                      0.16
                    )}`,
                  }
                : { p: 2, minWidth: 320 },
          },
          // The popover focuses its paper, not the editor. Land in the search
          // or the selected option, else the first input or button.
          transition: {
            onEntered: (node) =>
              (
                node.querySelector<HTMLElement>('input') ??
                node.querySelector<HTMLElement>('.Mui-selected') ??
                node.querySelector<HTMLElement>('[role="menuitem"], button')
              )?.focus(),
          },
        }}
      >
        {openFilter && (
          <FilterSwitch
            filter={openFilter}
            onChange={onChange}
            inline
            onDone={close}
          />
        )}
      </Popover>
    </Stack>
  );
};

interface FilterPillProps {
  filter: FilterDefinition<unknown>;
  open: boolean;
  onOpen: (el: HTMLElement) => void;
}

const FilterPill: FC<FilterPillProps> = (props) =>
  props.filter.type === 'boolean' ? (
    <TogglePill filter={props.filter} />
  ) : (
    <EditorPill {...props} />
  );

const EditorPill: FC<FilterPillProps> = ({ filter, open, onOpen }) => {
  const label = useFilterLabel(filter);
  const set = isFilterSet(filter);
  const clearable = set && canUnsetFilter(filter);
  // Anchor on the chip root, not the click target: the chevron icon unmounts
  // once the filter is set, and a detached anchor sends the popover to 0,0.
  const ref = useRef<HTMLDivElement>(null);
  const openPill = () => ref.current && onOpen(ref.current);
  return (
    <Chip
      ref={ref}
      label={label}
      variant="outlined"
      color={set || open ? 'primary' : 'default'}
      onClick={openPill}
      onDelete={clearable ? () => unsetFilter(filter) : openPill}
      deleteIcon={
        clearable ? undefined : (
          <Iconify icon="eva:chevron-down-fill" width={18} />
        )
      }
      sx={(theme) =>
        set
          ? { bgcolor: alpha(theme.palette.primary.main, 0.08) }
          : { bgcolor: 'background.paper' }
      }
    />
  );
};
